<template>
  <div class="pagination-wrapper">
    <ElPagination
      v-model:current-page="currentPage"
      v-model:page-size="pageSize"
      :page-sizes="pageSizes"
      :size="size"
      :disabled="disabled"
      :background="background"
      :layout="layout"
      :total="total"
      :page-count="pageCount"
      :pager-count="pagerCount"
      :default-current-page="defaultCurrentPage"
      :default-page-size="defaultPageSize"
      :small="small"
      :hide-on-single-page="hideOnSinglePage"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      @prev-click="handlePrevClick"
      @next-click="handleNextClick"
    />

    <!-- 分页信息显示 -->
    <div v-if="showInfo" class="pagination-info">
      <span class="pagination-info-text">
        共 {{ total }} 条记录，每页 {{ pageSize }} 条，
        第 {{ currentPage }} / {{ Math.ceil(total / pageSize) || 1 }} 页
      </span>
    </div>

    <!-- 快速跳转 -->
    <div v-if="showJumper" class="pagination-jumper">
      <span class="jumper-text">跳至</span>
      <ElInput
        v-model="jumpPage"
        class="jumper-input"
        size="small"
        type="number"
        :min="1"
        :max="Math.ceil(total / pageSize) || 1"
        @keyup.enter="handleJump"
      />
      <span class="jumper-text">页</span>
      <ElButton
        size="small"
        type="primary"
        @click="handleJump"
      >
        确定
      </ElButton>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Pagination 分页组件
 * 基于 Element Plus Pagination 组件封装
 * @author AI Assistant
 * @date 2024-01-01
 */

import { ref, computed, watch } from 'vue'
import type { ComponentSize } from 'element-plus'

// 接口定义
interface Props {
  /** 当前页数 */
  modelValue?: number
  /** 每页显示条目个数 */
  pageSize?: number
  /** 每页显示个数选择器的选项设置 */
  pageSizes?: number[]
  /** 组件大小 */
  size?: ComponentSize
  /** 是否禁用 */
  disabled?: boolean
  /** 是否为分页按钮添加背景色 */
  background?: boolean
  /** 组件布局，子组件名用逗号分隔 */
  layout?: string
  /** 总条目数 */
  total?: number
  /** 总页数 */
  pageCount?: number
  /** 设置最大页码按钮数 */
  pagerCount?: number
  /** 默认的当前页数 */
  defaultCurrentPage?: number
  /** 默认的每页显示条目个数 */
  defaultPageSize?: number
  /** 是否使用小型分页样式 */
  small?: boolean
  /** 只有一页时是否隐藏 */
  hideOnSinglePage?: boolean
  /** 是否显示分页信息 */
  showInfo?: boolean
  /** 是否显示快速跳转 */
  showJumper?: boolean
}

interface Emits {
  /** 当前页变化 */
  'update:modelValue': [page: number]
  /** 每页条数变化 */
  'size-change': [size: number]
  /** 当前页变化 */
  'current-change': [page: number]
  /** 上一页点击 */
  'prev-click': [page: number]
  /** 下一页点击 */
  'next-click': [page: number]
  /** 分页变化（包含页码和每页条数） */
  'pagination-change': [pagination: { page: number; size: number }]
}

// 定义 props
const props = withDefaults(defineProps<Props>(), {
  modelValue: 1,
  pageSize: 10,
  pageSizes: () => [10, 20, 50, 100],
  size: 'default',
  disabled: false,
  background: true,
  layout: 'total, sizes, prev, pager, next, jumper',
  total: 0,
  pageCount: undefined,
  pagerCount: 7,
  defaultCurrentPage: 1,
  defaultPageSize: 10,
  small: false,
  hideOnSinglePage: false,
  showInfo: false,
  showJumper: false
})

// 定义 emits
const emit = defineEmits<Emits>()

// 内部状态
const currentPage = computed({
  get: () => props.modelValue,
  set: (value: number) => {
    emit('update:modelValue', value)
  }
})

const pageSize = ref(props.pageSize)
const jumpPage = ref<number>()

// 监听 pageSize 变化
watch(
  () => props.pageSize,
  (newVal) => {
    pageSize.value = newVal
  },
  { immediate: true }
)

/**
 * 每页条数变化处理
 */
const handleSizeChange = (size: number): void => {
  pageSize.value = size

  // 如果当前页超出范围，调整到最后一页
  const maxPage = Math.ceil(props.total / size) || 1
  if (currentPage.value > maxPage) {
    currentPage.value = maxPage
  }

  emit('size-change', size)
  emit('pagination-change', {
    page: currentPage.value,
    size
  })
}

/**
 * 当前页变化处理
 */
const handleCurrentChange = (page: number): void => {
  emit('current-change', page)
  emit('pagination-change', {
    page,
    size: pageSize.value
  })
}

/**
 * 上一页点击处理
 */
const handlePrevClick = (page: number): void => {
  emit('prev-click', page)
}

/**
 * 下一页点击处理
 */
const handleNextClick = (page: number): void => {
  emit('next-click', page)
}

/**
 * 快速跳转处理
 */
const handleJump = (): void => {
  if (!jumpPage.value) return

  const maxPage = Math.ceil(props.total / pageSize.value) || 1
  const targetPage = Math.max(1, Math.min(jumpPage.value, maxPage))

  currentPage.value = targetPage
  jumpPage.value = undefined
}

/**
 * 重置到第一页
 */
const resetToFirstPage = (): void => {
  currentPage.value = 1
}

/**
 * 跳转到指定页
 */
const goToPage = (page: number): void => {
  const maxPage = Math.ceil(props.total / pageSize.value) || 1
  const targetPage = Math.max(1, Math.min(page, maxPage))
  currentPage.value = targetPage
}

/**
 * 获取分页信息
 */
const getPaginationInfo = () => {
  const maxPage = Math.ceil(props.total / pageSize.value) || 1
  return {
    currentPage: currentPage.value,
    pageSize: pageSize.value,
    total: props.total,
    totalPages: maxPage,
    hasNext: currentPage.value < maxPage,
    hasPrev: currentPage.value > 1
  }
}

// 暴露方法
defineExpose({
  resetToFirstPage,
  goToPage,
  getPaginationInfo
})
</script>

<style scoped>
.pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 0;
}

.pagination-info {
  display: flex;
  align-items: center;
}

.pagination-info-text {
  font-size: 14px;
  color: var(--el-text-color-regular);
  white-space: nowrap;
}

.pagination-jumper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.jumper-text {
  font-size: 14px;
  color: var(--el-text-color-regular);
  white-space: nowrap;
}

.jumper-input {
  width: 60px;
}

/* 自定义样式变量 */
:deep(.el-pagination) {
  --el-pagination-font-size: 14px;
  --el-pagination-bg-color: var(--el-fill-color-blank);
  --el-pagination-text-color: var(--el-text-color-primary);
  --el-pagination-border-radius: 4px;
  --el-pagination-button-color: var(--el-text-color-primary);
  --el-pagination-button-bg-color: var(--el-fill-color-blank);
  --el-pagination-button-disabled-color: var(--el-text-color-placeholder);
  --el-pagination-button-disabled-bg-color: var(--el-fill-color-blank);
  --el-pagination-hover-color: var(--el-color-primary);
}

:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next) {
  padding: 0 8px;
  min-width: 32px;
  height: 32px;
  line-height: 30px;
  border: 1px solid var(--el-border-color);
  border-radius: var(--el-pagination-border-radius);
  background-color: var(--el-pagination-button-bg-color);
  color: var(--el-pagination-button-color);
  transition: all 0.2s;
}

:deep(.el-pagination .btn-prev:hover),
:deep(.el-pagination .btn-next:hover) {
  color: var(--el-pagination-hover-color);
  border-color: var(--el-pagination-hover-color);
}

:deep(.el-pagination .btn-prev:disabled),
:deep(.el-pagination .btn-next:disabled) {
  color: var(--el-pagination-button-disabled-color);
  background-color: var(--el-pagination-button-disabled-bg-color);
  border-color: var(--el-border-color-light);
  cursor: not-allowed;
}

:deep(.el-pager li) {
  min-width: 32px;
  height: 32px;
  line-height: 30px;
  border: 1px solid var(--el-border-color);
  border-radius: var(--el-pagination-border-radius);
  background-color: var(--el-pagination-button-bg-color);
  color: var(--el-pagination-button-color);
  margin: 0 2px;
  transition: all 0.2s;
}

:deep(.el-pager li:hover) {
  color: var(--el-pagination-hover-color);
  border-color: var(--el-pagination-hover-color);
}

:deep(.el-pager li.is-active) {
  color: var(--el-color-white);
  background-color: var(--el-color-primary);
  border-color: var(--el-color-primary);
}

:deep(.el-pagination__sizes) {
  margin-right: 16px;
}

:deep(.el-pagination__total) {
  margin-right: 16px;
  font-weight: normal;
  color: var(--el-text-color-regular);
}

:deep(.el-pagination__jump) {
  margin-left: 16px;
  color: var(--el-text-color-regular);
}

:deep(.el-pagination__jump .el-input__inner) {
  width: 50px;
  margin: 0 8px;
  text-align: center;
}

/* 小尺寸样式 */
:deep(.el-pagination.is-small .btn-prev),
:deep(.el-pagination.is-small .btn-next),
:deep(.el-pagination.is-small .el-pager li) {
  min-width: 28px;
  height: 28px;
  line-height: 26px;
  font-size: 12px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .pagination-wrapper {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .pagination-info,
  .pagination-jumper {
    justify-content: center;
  }

  :deep(.el-pagination) {
    justify-content: center;
  }

  :deep(.el-pagination__sizes),
  :deep(.el-pagination__total),
  :deep(.el-pagination__jump) {
    margin: 0 8px;
  }
}

@media (max-width: 480px) {
  :deep(.el-pagination) {
    --el-pagination-font-size: 12px;
  }

  :deep(.el-pagination .btn-prev),
  :deep(.el-pagination .btn-next),
  :deep(.el-pager li) {
    min-width: 28px;
    height: 28px;
    line-height: 26px;
    font-size: 12px;
  }

  .pagination-info-text,
  .jumper-text {
    font-size: 12px;
  }
}

/* 暗色主题适配 */
@media (prefers-color-scheme: dark) {
  :deep(.el-pagination) {
    --el-pagination-bg-color: var(--el-bg-color-page);
    --el-pagination-button-bg-color: var(--el-fill-color-dark);
  }
}

/* 动画效果 */
:deep(.el-pager li),
:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next) {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

:deep(.el-pager li:active),
:deep(.el-pagination .btn-prev:active),
:deep(.el-pagination .btn-next:active) {
  transform: scale(0.95);
}
</style>
